import type { BoostPolicyBrandingData, BoostPolicyFormData, BoostPolicyKnowledgeDocument, BoostPolicyRecord, SelectedTemplateData } from "../types/boostPolicy";
import { supabaseRevisr } from "../utils/supabaseRevisrClient";
import { supabase } from "@foundation/utils/supabaseClient";
import { fetchKnowledgeDocsWithTags, mapToBoostPolicyDoc } from "./knowledgeDocsService";
import { fetchAllDocCategoryMappings } from "./contextCategoryService";
import type { DocCategoryMapping } from "./contextCategoryService";
import { getActiveWorkspaceId } from "../../../header/activeWorkspace";

// Feature flag: when true, loadDefaultTemplate() returns the hardcoded default template.
// The default template is from revisr.policy_templates — separate from library documents.
const DEFAULT_TEMPLATE_ID = "2f842370-d45c-4bf8-ad61-ae5ff6a194ce";
const USE_DEFAULT_TEMPLATE = import.meta.env.VITE_USE_DEFAULT_TEMPLATE === "true";

export const loadBoostPolicyKnowledgeDocuments = async (): Promise<BoostPolicyKnowledgeDocument[]> => {
  const [docs, mappings] = await Promise.all([
    fetchKnowledgeDocsWithTags(),
    fetchAllDocCategoryMappings().catch(() => [] as DocCategoryMapping[]),
  ]);

  const mappingByDocId = new Map(mappings.map((m) => [m.docId, m]));

  const published = docs.filter(
    (doc) => !doc.metadata?.status || doc.metadata.status === 'published',
  );

  return published.map((doc) => {
    const m = mappingByDocId.get(doc.id);
    return {
      ...mapToBoostPolicyDoc(doc),
      categoryMapping: m ? { categoryId: m.categoryId, subcategoryId: m.subcategoryId } : null,
    };
  });
};

/**
 * Builds the payload for /api/revisr/brand-policy/stream.
 * The backend fetches the full library document content from DB using selectedLibraryDocId.
 * selectedSkillIds are resolved to system prompts on the backend (altzero.base_ai_skills).
 */
export const createBoostPolicyBrandPayload = (
  data: BoostPolicyFormData,
  brandingData: BoostPolicyBrandingData | null,
  selectedTemplate: { id: string; pages: unknown[] | null; sectionNames: string[] } | null = null,
): {
  selectedLibraryDocId: string;
  selectedSkillIds: string[];
  policyName: string;
  organizationContext: string;
  industryTags: BoostPolicyFormData["industryTags"];
  regionTags: BoostPolicyFormData["regionTags"];
  workforceSize: string;
  brandingData: BoostPolicyBrandingData | null;
  selectedTemplate: { id: string; pages: unknown[] | null; sectionNames: string[] } | null;
} => {
  return {
    selectedLibraryDocId: data.selectedKnowledgeDocument?.id ?? "",
    selectedSkillIds: data.selectedSkillIds,
    policyName: data.policyName,
    organizationContext: data.organizationContext,
    industryTags: data.industryTags,
    regionTags: data.regionTags,
    workforceSize: data.workforceSize,
    brandingData,
    selectedTemplate,
  };
};

/**
 * Loads the default policy template from revisr.policy_templates + templates storage bucket.
 * NOTE: This is a policy template (document structure), NOT a library document.
 * Returns template data when VITE_USE_DEFAULT_TEMPLATE=true, null otherwise.
 */
export const loadDefaultTemplate = async (): Promise<{
  id: string;
  pages: unknown[] | null;
  sectionNames: string[];
} | null> => {
  if (!USE_DEFAULT_TEMPLATE) return null;

  try {
    const { data, error } = await supabaseRevisr
      .from("policy_templates")
      .select("id, name, sections, bucket_path")
      .eq("id", DEFAULT_TEMPLATE_ID)
      .single();

    if (error || !data) {
      console.warn("[BoostPolicy] Default template not found:", DEFAULT_TEMPLATE_ID);
      return null;
    }

    let pages: unknown[] | null = null;
    if (data.bucket_path) {
      const { data: fileData } = await supabaseRevisr.storage
        .from("knowledge_docs")
        .download(data.bucket_path);

      if (fileData) {
        const text = await fileData.text();
        try {
          const parsed = JSON.parse(text);
          // Handle both { pages: [...] } and { editorState: { pages: [...] } } formats
          if (Array.isArray(parsed?.pages)) {
            pages = parsed.pages;
          } else if (Array.isArray(parsed?.editorState?.pages)) {
            pages = parsed.editorState.pages;
          }
        } catch {
          // Content is markdown, not JSON editor format — pages stay null
        }
      }
    }

    return {
      id: data.id,
      pages,
      sectionNames: data.sections || [],
    };
  } catch (error) {
    console.error("[BoostPolicy] Failed to load default template:", error);
    return null;
  }
};

/**
 * Loads a policy template by ID from revisr.policy_templates + templates storage bucket.
 * Used to restore template data when resuming a policy from DB (no session available).
 */
export const loadPolicyTemplateById = async (
  templateId: string,
): Promise<{ id: string; pages: unknown[] | null; sectionNames: string[] } | null> => {
  try {
    const { data, error } = await supabaseRevisr
      .from("policy_templates")
      .select("id, name, sections, bucket_path, content")
      .eq("id", templateId)
      .single();

    if (error || !data) {
      console.warn("[BoostPolicy] Template not found:", templateId);
      return null;
    }

    let pages: unknown[] | null = null;

    // Priority 1: storage bucket (visual editor templates save JSON here)
    if (data.bucket_path) {
      const { data: fileData } = await supabaseRevisr.storage
        .from("knowledge_docs")
        .download(data.bucket_path);

      if (fileData) {
        const text = await fileData.text();
        try {
          const parsed = JSON.parse(text);
          // Handle both { pages: [...] } and { editorState: { pages: [...] } } formats
          if (Array.isArray(parsed?.pages)) {
            pages = parsed.pages;
          } else if (Array.isArray(parsed?.editorState?.pages)) {
            pages = parsed.editorState.pages;
          }
        } catch {
          // Not JSON — pages stay null
        }
      }
    }

    // Priority 2: content column (templates stored inline as ModernTemplateEditor JSON)
    if (!pages && data.content) {
      try {
        const parsed = JSON.parse(data.content as string);
        if (Array.isArray(parsed?.pages)) {
          pages = parsed.pages;
        } else if (Array.isArray(parsed?.editorState?.pages)) {
          pages = parsed.editorState.pages;
        }
      } catch {
        // HTML or markdown — not usable as editor pages
      }
    }

    return {
      id: data.id,
      pages,
      sectionNames: data.sections || [],
    };
  } catch (error) {
    console.error("[BoostPolicy] Failed to load template by ID:", error);
    return null;
  }
};

/**
 * Fetches minimal template metadata (id, name, content, bucket_path) by ID.
 * Used to restore selectedTemplateData when resuming a boost policy from the database
 * after the in-memory / sessionStorage state has been cleared.
 */
export const getTemplateMetadataById = async (templateId: string): Promise<SelectedTemplateData | null> => {
  try {
    const { data, error } = await supabaseRevisr
      .from("policy_templates")
      .select("id, name, content, bucket_path")
      .eq("id", templateId)
      .single();

    if (error || !data) {
      console.warn("[BoostPolicy] Template metadata not found:", templateId);
      return null;
    }

    return {
      id: data.id,
      name: data.name,
      content: (data.content as string) ?? null,
      bucket_path: (data.bucket_path as string) ?? null,
    };
  } catch {
    return null;
  }
};

/**
 * Resolves which template to use for generation.
 * - When VITE_USE_DEFAULT_TEMPLATE=true  → always uses the hardcoded default template ID.
 * - When VITE_USE_DEFAULT_TEMPLATE=false → uses the template ID selected by the user
 *   (userSelectedTemplateId), or null if the user chose to generate without a template.
 */
export const loadSelectedTemplate = async (
  userSelectedTemplateId: string | null,
): Promise<{ id: string; pages: unknown[] | null; sectionNames: string[] } | null> => {
  if (USE_DEFAULT_TEMPLATE) {
    return loadDefaultTemplate();
  }
  if (!userSelectedTemplateId) return null;
  return loadPolicyTemplateById(userSelectedTemplateId);
};

// ─── Boost Policy Draft CRUD (interim save) ──────────────────────────────────

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

const getBoostPolicyHeaders = async (): Promise<Record<string, string>> => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const userId = session?.user?.id;

  const orgId = getActiveWorkspaceId() ?? "";

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "x-user-id": userId || "",
    "x-org-id": orgId,
  };
};

export const createBoostPolicyDraft = async (params: {
  policyStoreId: string;
  selectedKnowledgeDocument: BoostPolicyKnowledgeDocument;
}): Promise<{ id: string }> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/create`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      policy_store_id: params.policyStoreId,
      template_store_id: params.policyStoreId,
      selected_knowledge_document: params.selectedKnowledgeDocument,
    }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({})) as { error?: string; code?: string; reason?: string; current?: number; limit?: number };
    if (resp.status === 403 && err.code?.includes('LIMIT_REACHED')) {
      const limitError = new Error('Policy limit reached');
      (limitError as any).isLimitReached = true;
      (limitError as any).current = err.current;
      (limitError as any).limit = err.limit;
      throw limitError;
    }
    throw new Error(err.error || "Failed to create boost policy draft");
  }
  return resp.json();
};

export const updateBoostPolicyStep = async (
  policyId: string,
  updates: Record<string, unknown>,
): Promise<void> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/${policyId}/step`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(updates),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || "Failed to update boost policy step");
  }
};

export const getBoostPolicyById = async (
  policyId: string,
): Promise<BoostPolicyRecord> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/${policyId}`, {
    method: "GET",
    headers,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || "Failed to fetch boost policy");
  }
  return resp.json();
};

export const fetchEditorContent = async (
  policyId: string,
): Promise<{ pages: unknown[]; zoom: number; footer?: { classification?: string; policyName?: string; orgName?: string; date?: string; note?: string }; includeCoverToc?: boolean } | null> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/${policyId}/editor-content`, {
    method: "GET",
    headers,
  });
  if (resp.status === 404) return null; // no editor content saved yet
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || "Failed to fetch editor content");
  }
  return resp.json();
};

/**
 * Uploads the generated markdown to the knowledge_docs bucket and records
 * the file path in policy_markdown_url. Also sets current_step to 5.
 * Fire-and-forget safe — the caller should .catch() errors.
 */
export const saveGeneratedContent = async (
  policyId: string,
  markdown: string,
  policyName: string,
  resolvedPages?: unknown[],
): Promise<void> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/${policyId}/save-generated`, {
    method: "POST",
    headers,
    body: JSON.stringify({ markdown, policyName, resolvedPages }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || "Failed to save generated content");
  }
};

/**
 * Downloads the generated markdown stored in knowledge_docs for the given policy.
 * Also returns resolved template pages if they were saved during generation.
 * Returns null if no content has been saved yet or if the fetch fails.
 */
export const fetchSavedContent = async (policyId: string): Promise<{
  content: string;
  resolvedPages: unknown[] | null;
} | null> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/${policyId}/content`, {
    method: "GET",
    headers,
  });
  if (!resp.ok) return null;
  const data = await resp.json() as { content?: string; resolvedPages?: unknown[] };
  if (!data.content) return null;
  return {
    content: data.content,
    resolvedPages: Array.isArray(data.resolvedPages) ? data.resolvedPages : null,
  };
};

// ─── Policy approval workflow ────────────────────────────────────────────────
// Active only when VITE_POLICY_APPROVAL_REQUIRED / POLICY_APPROVAL_REQUIRED env
// flags are on. Members request org_admin approval before a policy can be saved as
// completed or exported. See Migrations/032_add_policy_approval_status.sql.

export type PolicyApprovalState = 'none' | 'pending' | 'approved' | 'rejected';

export type PolicyApprovalSummary = {
  id: string;
  policy_name: string;
  status: string;
  workflow_type?: string;
  requested_by: string | null;
  /** Display name of the requester (resolved server-side from base_profiles). */
  requested_by_name: string | null;
  requested_at: string | null;
  updated_at: string | null;
};

/**
 * Derive the approval state from a policy record's status + metadata.approval.
 * Used across the generation page, editor, and Your Policies list to gate the
 * Save/Export buttons consistently.
 */
export const getApprovalState = (record: {
  status?: string;
  metadata?: Record<string, unknown> | null;
} | null): PolicyApprovalState => {
  if (!record) return 'none';
  const approval = (record.metadata as { approval?: { status?: string } } | null | undefined)?.approval;
  if (approval?.status === 'approved') return 'approved';
  if (approval?.status === 'rejected') return 'rejected';
  if (record.status === 'pending_approval') return 'pending';
  return 'none';
};

/**
 * Member → request admin approval. Accepts the same editor payload as /save so the
 * member's edited content is persisted before the status flips to pending_approval.
 * Pages/markdown are optional (the generation page calls it with no editor payload).
 */
export const requestApproval = async (
  policyId: string,
  payload: {
    title?: string;
    pages?: unknown[];
    zoom?: number;
    markdown?: string;
    footer?: { classification?: string };
    includeCoverToc?: boolean;
  } = {},
): Promise<{ success: boolean; status: string; approvalSkipped?: boolean }> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/${policyId}/request-approval`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || "Failed to request approval");
  }
  return resp.json();
};

/** Org admin → list the org's pending approval requests. */
export const listPendingApprovals = async (): Promise<PolicyApprovalSummary[]> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/approvals`, {
    method: "GET",
    headers,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || "Failed to list approvals");
  }
  const data = await resp.json() as { approvals?: PolicyApprovalSummary[] };
  return data.approvals ?? [];
};

/** Org admin → fetch a policy record + its markdown for review. */
export const getApprovalDetail = async (
  policyId: string,
): Promise<{ record: Record<string, unknown>; content: string | null }> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/${policyId}/approval`, {
    method: "GET",
    headers,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || "Failed to fetch approval detail");
  }
  return resp.json();
};

/** Org admin → approve a requested policy. */
export const approvePolicy = async (policyId: string): Promise<{ success: boolean; status: string }> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/${policyId}/approve`, {
    method: "POST",
    headers,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || "Failed to approve policy");
  }
  return resp.json();
};

/** Org admin → reject a requested policy with an optional reason. */
export const rejectPolicy = async (
  policyId: string,
  reason?: string,
): Promise<{ success: boolean; status: string }> => {
  const headers = await getBoostPolicyHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/${policyId}/reject`, {
    method: "POST",
    headers,
    body: JSON.stringify({ reason }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || "Failed to reject policy");
  }
  return resp.json();
};
